import { useCallback, useEffect, useState } from "react";
import type { GuardianRule } from "@/services/api/guardiansv2/types";
import type { UseRulesResult } from "./useRules";
import { AddRuleMenu, IgnoreRuleRow, LlmRuleRow, isIgnoreRule, isLlmRule } from "./RuleRows";
import { RulesTaskTypeSidebar } from "./RulesTaskTypeSidebar";
import { GenerateRulesDialog } from "./GenerateRulesDialog";

function newRuleId(): string {
  return crypto.randomUUID().slice(0, 12);
}

function withRuleId(rule: GuardianRule): GuardianRule {
  return rule.id ? rule : { ...rule, id: newRuleId() };
}

export interface RulesPaneProps {
  rulesState: UseRulesResult;
  highlightRuleId?: string | null;
}

export function RulesPane({ rulesState, highlightRuleId }: RulesPaneProps) {
  const {
    catalogueGroups,
    ungroupedCatalogue,
    catalogueKey,
    catalogueClientId,
    setCatalogueKey,
    rules,
    ruleStats,
    canEditRules,
    setRules,
    isDirty,
    loading,
    saving,
    error,
    save,
    reset,
  } = rulesState;

  const [generateOpen, setGenerateOpen] = useState(false);
  const [editingRuleIds, setEditingRuleIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!highlightRuleId) return;
    setEditingRuleIds((prev) => new Set(prev).add(highlightRuleId));
  }, [highlightRuleId, catalogueKey]);

  const startEditing = useCallback((ruleId: string) => {
    setEditingRuleIds((prev) => new Set(prev).add(ruleId));
  }, []);

  const finishEditing = useCallback((ruleId: string) => {
    setEditingRuleIds((prev) => {
      const next = new Set(prev);
      next.delete(ruleId);
      return next;
    });
  }, []);

  const updateRule = (index: number, next: GuardianRule) => {
    setRules((prev) => {
      const copy = [...prev];
      copy[index] = next;
      return copy;
    });
  };

  const removeRule = (index: number) => {
    setRules((prev) => {
      const removed = prev[index];
      if (removed?.id) {
        finishEditing(removed.id);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const addRule = (rule: GuardianRule) => {
    const withId = withRuleId(rule);
    setRules((prev) => [...prev, withId]);
    startEditing(withId.id);
  };

  const addGeneratedRules = (generated: GuardianRule[]) => {
    const withIds = generated.map(withRuleId);
    setRules((prev) => [...prev, ...withIds]);
    setEditingRuleIds((prev) => {
      const next = new Set(prev);
      for (const rule of withIds) {
        next.add(rule.id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const saved = await save();
    if (saved) {
      setEditingRuleIds(new Set());
    }
  };

  const checkRules = rules.filter(isLlmRule);
  const ignoreRules = rules.filter(isIgnoreRule);

  return (
    <div className="flex h-full min-h-0" data-testid="rules-pane">
      <RulesTaskTypeSidebar
        groups={catalogueGroups}
        ungrouped={ungroupedCatalogue}
        catalogueKey={catalogueKey}
        onSelect={setCatalogueKey}
      />

      <div className="min-w-0 flex-1 overflow-y-auto p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Configure alerts</h2>
            <p className="text-xs text-slate-400">
              LLM checks for <span className="text-slate-300">{catalogueKey ?? "—"}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEditRules ? (
              <>
                <AddRuleMenu onAdd={addRule} onGenerate={() => setGenerateOpen(true)} />
                <button
                  type="button"
                  disabled={saving || !catalogueKey || !isDirty}
                  onClick={() => void handleSave()}
                  className="rounded-lg bg-slate-700 px-3 py-2 text-sm text-slate-100 hover:bg-slate-600 disabled:opacity-50"
                  data-testid="rules-save-button"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            ) : (
              <p className="text-xs text-slate-500">Global rules for this task type — editable by Ani staff only.</p>
            )}
          </div>
        </div>

        {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
        {loading ? (
          <p className="text-sm text-slate-400">Loading rules…</p>
        ) : (
          <>
            <div className="space-y-6">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Alert checks</h3>
                <div className="space-y-2">
                  {checkRules.length === 0 ? (
                    <p className="text-sm text-slate-500">No alert check rules yet. Add a rule or generate from a description.</p>
                  ) : (
                    rules.map((rule, index) =>
                      isLlmRule(rule) ? (
                        <LlmRuleRow
                          key={rule.id}
                          rule={rule}
                          stats={ruleStats[rule.id]}
                          readOnly={!canEditRules}
                          editing={editingRuleIds.has(rule.id)}
                          onChange={(next) => updateRule(index, next)}
                          onRemove={() => removeRule(index)}
                          onStartEdit={() => startEditing(rule.id)}
                          onFinishEdit={() => finishEditing(rule.id)}
                        />
                      ) : null
                    )
                  )}
                </div>
              </section>
              {ignoreRules.length > 0 ? (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Ignore rules</h3>
                  <div className="space-y-2">
                    {rules.map((rule, index) =>
                      isIgnoreRule(rule) ? (
                        <IgnoreRuleRow
                          key={rule.id}
                          rule={rule}
                          readOnly={!canEditRules}
                          editing={editingRuleIds.has(rule.id)}
                          onChange={(next) => updateRule(index, next)}
                          onRemove={() => removeRule(index)}
                          onStartEdit={() => startEditing(rule.id)}
                          onFinishEdit={() => finishEditing(rule.id)}
                        />
                      ) : null
                    )}
                  </div>
                </section>
              ) : null}
            </div>
            {canEditRules ? (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  disabled={!isDirty || saving}
                  onClick={() => {
                    void reset();
                    setEditingRuleIds(new Set());
                  }}
                  className="text-xs text-slate-400 hover:text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="rules-reset-button"
                >
                  Reset
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {generateOpen && canEditRules ? (
        <GenerateRulesDialog
          catalogueKey={catalogueKey}
          clientId={catalogueClientId}
          onClose={() => setGenerateOpen(false)}
          onGenerated={addGeneratedRules}
        />
      ) : null}
    </div>
  );
}
